import React, { useMemo } from 'react'
import 'styles/components/DefaultChartConfig.sass';
import {LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer} from 'recharts';
import LegacyChart from 'interface/LegacyChart';
import processChartData from 'utils/processChartData';
import ZERONE_COLORS from 'constants/data/global/ZeroneColors';

interface LineChartProps {
    chart: LegacyChart;
    height?: number;
    className?: string;
}

/**
 * LineChart component renders a line chart using Recharts.
 * It processes the chart data and displays it with customizable axes, tooltips, and legends.
 *
 * @param {LineChartProps} props - The properties for the LineChart component.
 * 
 * @returns {JSX.Element} The rendered LineChart component.
 */
const LineChart: React.FC<LineChartProps> = ({ chart, height = 400, className = '' }) => {
    const { processedData, hasLeftAxis, hasRightAxis, seriesConfig, formatAxisLabel, formatTooltipValue } = useMemo(() => processChartData(chart), [chart]);

    // If no chart data is available, return a placeholder
    if (!chart || !chart.series || chart.series.length === 0) {
        return <div className={`line-chart ${className}`}>No data available</div>;
    }

    return (
        <div className='chart-container' style={{ height: `${height}px` }}>
            <ResponsiveContainer width="100%" height="100%">
                <RechartsLineChart
                    data={processedData}
                    margin={{ top: 20, right: hasRightAxis ? 30 : 20, left: 20, bottom: 60 }}
                >
                    <CartesianGrid 
                        strokeDasharray={"3 3"}
                        stroke={ZERONE_COLORS.gridLines || '#CFE6F3'}
                        opacity={0.8}
                    />

                    <XAxis
                        dataKey={'x'}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: ZERONE_COLORS.textLight }}
                        label={{
                            value: chart.xAxisLabel || '',
                            position: 'insideBottom',
                            offset: -5,
                            style: { textAnchor: 'middle', fontSize: 14, fill: ZERONE_COLORS.textPrimary }
                        }}
                    />

                    {hasLeftAxis && (
                        <YAxis
                            yAxisId={'left'}
                            orientation={'left'}
                            axisLine={false}
                            tickLine={false}
                            tick ={{ fontSize: 12, fill: ZERONE_COLORS.textLight }}
                            label={{
                                value: formatAxisLabel('left'),
                                angle: -90,
                                position: 'insideLeft',
                                offset: -10,
                                style: { textAnchor: 'middle', fontSize: 14, fill: ZERONE_COLORS.textPrimary }
                            }}
                        />
                    )}

                    {hasRightAxis && (
                        <YAxis
                            yAxisId={'right'}
                            orientation={'right'}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: ZERONE_COLORS.textLight }}
                            label={{
                                value: formatAxisLabel('right'),
                                angle: 90,
                                position: 'insideRight',
                                offset: -10,
                                style: { textAnchor: 'middle', fontSize: 14, fill: ZERONE_COLORS.textPrimary }
                            }}
                        />
                    )}

                    <Tooltip
                        contentStyle={{
                            backgroundColor: ZERONE_COLORS.background,
                            borderColor: `1px solid ${ZERONE_COLORS.border}`,
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            padding: '10px',
                            fontSize: '14px'
                        }}
                        labelStyle={{
                            color: ZERONE_COLORS.textPrimary,
                            fontWeight: 600
                        }}
                        formatter={formatTooltipValue}
                    />

                    <Legend
                        wrapperStyle={{
                            paddingTop: '10px',
                            fontSize: '14px',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            color: ZERONE_COLORS.textPrimary
                        }}
                        iconType="line"
                        iconSize={12}
                    />

                    {seriesConfig.map((series) => (
                        <Line
                            key={series.dataKey}
                            dataKey={series.dataKey}
                            name={series.name}
                            stroke={series.color || ZERONE_COLORS.primary}
                            yAxisId={series.axis}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            animationDuration={800}
                            animationEasing='ease-in-out'
                            type="monotone"
                            unit={series.unit}
                        />
                    ))}
                </RechartsLineChart>
            </ResponsiveContainer>
        </div>
    )
}

export default LineChart;